import { useContext, useEffect, useMemo, useState } from 'react';
import { first } from 'lodash';

import { TrackingContext, useSiteSettings } from '@gn-audio/jss-components/utils/contexts';
import { AvailabilityState, GtmEventType, GtmPayload } from '@gn-audio/jss-core';

import { CustomizerConfig } from 'api/jabraPublicApi';
import { getSfccProductPrices } from 'api/pricesApi/getSfccProductPrices';
import { FamilyGroup } from 'api/productCatalogueApi';
import { AccessoriesPicker } from 'composables/Product/Buy/Accessories/AccessoriesPicker';
import { getSfccGroupAccessories } from 'composables/Product/Buy/Accessories/getSfccGroupAccessories';
import { CustomizerGroupFilter } from 'composables/Product/Buy/CustomizerFilters/CustomizerGroupFilter';
import { sortGroupsByAvailability } from 'composables/Product/Buy/customizerHelpers';
import { CustomizerTotal } from 'composables/Product/Buy/CustomizerTotal';
import { ImageSlider } from 'composables/Product/Buy/ImageSlider/ImageSlider';
import { useUrlSku } from 'composables/Product/Buy/ProductCustomizer.hooks';
import { ProductInfo } from 'composables/Product/Buy/ProductInfo';
import { useAsync } from 'utils/hooks/useAsync';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { isNotNil } from 'utils/utils';

interface ProductCustomizerProps {
  uniqueSellingPoint: string;
  groups: FamilyGroup[];
  config: CustomizerConfig;
  isMainHeading: boolean;
}

export const ProductCustomizer = ({
  config,
  uniqueSellingPoint,
  groups,
  isMainHeading,
}: ProductCustomizerProps) => {
  const { t } = useFallbackTranslation();
  const { ecom, sfcc } = useSiteSettings();
  const { gtmEvent } = useContext(TrackingContext);

  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState<number[]>([]);
  const skus = useMemo(() => groups.map((group) => group.sku), [groups]);

  const groupsNeedSorting = !config.keepGroupSortOrder && ecom?.ecomEnabled;

  const [sku, setSku] = useUrlSku();

  const groupPrices = useAsync(async () => {
    if (!ecom?.ecomEnabled) {
      return null;
    }

    return getSfccProductPrices(skus);
  }, [skus, ecom?.ecomEnabled]);

  const firstGroup = useMemo(() => {
    if (groupsNeedSorting && groupPrices) {
      const sortedGroups = sortGroupsByAvailability(groups, groupPrices);
      return first(sortedGroups);
    } else {
      return first(groups);
    }
  }, [groups, groupPrices, groupsNeedSorting]);

  const selectedGroup = groups.find((group) => group.sku === sku);

  useEffect(() => {
    if (!selectedGroup && !!firstGroup?.sku) {
      setSku(firstGroup.sku);
    }
  }, [selectedGroup, firstGroup?.sku, setSku]);

  const allAccessories = useAsync(async () => {
    if (selectedGroup?.groupId === undefined || !ecom?.ecomEnabled) {
      return [];
    }

    return await getSfccGroupAccessories(selectedGroup?.groupId);
  }, [selectedGroup?.groupId, sfcc, ecom?.ecomEnabled]);

  const accessories = useMemo(() => {
    return (allAccessories ?? []).filter((accessory) =>
      config.accessoryTypes.some((type) => type.value === accessory.product.type)
    );
  }, [allAccessories, config.accessoryTypes]);

  useEffect(() => {
    const newPossibleAccessoryIds =
      accessories?.map((accessory) => accessory.product.productId) ?? [];

    setSelectedAccessoryIds((ids) => {
      return ids.filter((id) => newPossibleAccessoryIds.includes(id));
    });
  }, [accessories]);

  const selectedProductPrice =
    groupPrices !== null ? groupPrices?.find((p) => p.sku === sku) : null;

  const eCommercePayload: GtmPayload = useMemo(
    () => ({
      event: GtmEventType.ViewItem,
      ecommerce: {
        currency: selectedProductPrice?.currency || '',
        items: (groupPrices ?? [])
          .filter((price) => groups.some((group) => group.sku === price.sku))
          .map((price) => {
            const group = groups.find((group) => group.sku === price.sku);
            return {
              item_name: group?.name || '',
              item_id: group?.sku || '',
              price: price.discountedPrice || '0.00',
              item_brand:
                group?.attributes.find((attr) => attr.key === 'Product_Portfolio')?.value || '',
              item_category:
                group?.attributes.find((attr) => attr.key === 'Meta_Producttype')?.value || '',
              quantity: '1',
              currency: price.currency || '',
            };
          }),
      },
    }),
    [groups, groupPrices, selectedProductPrice]
  );
  useEffect(() => {
    if (eCommercePayload.ecommerce?.currency) {
      gtmEvent(eCommercePayload);
    }
  }, [groups, groupPrices, eCommercePayload, gtmEvent]);

  const handleSelectGroup = (group: FamilyGroup) => {
    setSku(group.sku);
  };

  const handleSelectAccessories = (accessoryIds: number[]) => {
    setSelectedAccessoryIds(accessoryIds);
  };

  const selectedAccessories = selectedAccessoryIds
    .map((id) => accessories?.find(({ product }) => product.productId === id))
    .filter(isNotNil);

  const isAvailable =
    selectedProductPrice?.availabilityState !== undefined
      ? selectedProductPrice.availabilityState < AvailabilityState.WhereToBuy
      : false;

  return (
    <article className="container-fluid flex w-full flex-col flex-wrap overflow-hidden xl:flex-row xl:items-center xl:justify-between">
      <aside
        className="max-w-full flex-1 grow-0 overflow-hidden xl:basis-6/12"
        aria-label={`${selectedGroup?.familyName} ${t('system.Illustrations')}`}
      >
        <ImageSlider group={selectedGroup} />
      </aside>
      <section
        className="container flex-1 grow-0 pb-6 pt-0 xl:m-0 xl:basis-5/12 xl:p-6 xl:pl-0"
        aria-label={`${t('system.Buy')} ${selectedGroup?.familyName}`}
      >
        <div>
          <ProductInfo
            group={selectedGroup}
            price={selectedProductPrice}
            uniqueSellingPoint={uniqueSellingPoint}
            isMainHeading={isMainHeading}
          />
          <hr className="my-6 border-gray-50" />

          {groups.length > 0 && config.filters.length > 0 && (
            <>
              <CustomizerGroupFilter
                groups={groups}
                filters={config.filters}
                onSelect={(group) => handleSelectGroup(group)}
                selectedGroup={selectedGroup}
              />
              <hr className="my-6 border-gray-50" />
            </>
          )}

          {accessories.length > 0 && config.showAccessories && isAvailable && (
            <>
              <AccessoriesPicker
                accessories={accessories}
                onSelectedAccessoriesChange={handleSelectAccessories}
                selectedAccessoryIds={selectedAccessoryIds}
              />
              <hr className="my-6 border-gray-50" />
            </>
          )}
          <CustomizerTotal
            price={selectedProductPrice}
            selectedGroup={selectedGroup}
            accessories={selectedAccessories}
          />
        </div>
      </section>
    </article>
  );
};
